import type {
  PortfolioAggregate,
  ProjectWithImpacts,
} from "../portfolio-dashboard/api/portfolio";
import type {
  AirshedAggregateResult,
  AirshedRegion,
  PortfolioLifetimeAggregate,
  PortfolioYearAggregate,
} from "./api";

export type Segment = "operational" | "procurement" | "both";
export type AnalysisBasis = "ctg" | "ops";
// Operational → lifetime (cumulative realized) or a single calendar year.
// Procurement → forward lifetime projection.
export type TimeBasis = "lifetime" | "single_year" | "projection";
export type RankingMetric = "dalys" | "pdfs" | "usd" | "per_mw";

export type SectionKey =
  | "cover"
  | "executive_summary"
  | "fuel_displacement"
  | "emissions_air"
  | "emissions_water"
  | "emissions_soil"
  | "emissions_resources"
  | "public_health"
  | "biodiversity"
  | "economic"
  | "spatial_map"
  | "ranking_absolute"
  | "ranking_per_mw"
  | "data_quality"
  | "methodology"
  | "glossary"
  | "closing";

export interface PortfolioReportOptions {
  filters: Record<string, string>;
  segment: Segment;
  analysis: AnalysisBasis;
  timeBasis: TimeBasis;
  year?: number; // when timeBasis === "single_year"
  lifetimeYears?: number; // when timeBasis === "projection"
  discountRate?: number;
  sections: Record<SectionKey, boolean>;
  rankingMetric: RankingMetric;
  topN: number; // 0 = all
  branding: {
    title: string;
    subtitle: string;
    period: string;
    confidential: boolean;
    confidentialText: string;
  };
  outputs: { pdf: boolean; pptx: boolean };
}

export interface PortfolioReportData {
  options: PortfolioReportOptions;
  generatedAt: string;
  // Full-period cradle-to-grave / ops aggregate (impacts, generation,
  // emissions, subcategories). Always fetched — drives detail pages.
  aggregate: PortfolioAggregate;
  // Headline tri-metric source, depends on timeBasis:
  headline: {
    basisLabel: string; // e.g. "Lifetime", "2025", "25-yr projection"
    dalys: number;
    pdfs: number;
    usd: number;
    generationMwh: number;
    isProjection: boolean;
    npv?: { dalys: number; pdfs: number; usd: number };
  };
  year?: PortfolioYearAggregate;
  lifetime?: PortfolioLifetimeAggregate;
  projects: ProjectWithImpacts[];
  netBurdenCount: number;
  spatial?: AirshedRegion[];
  /** Raw US-state FeatureCollection for choropleth rendering. */
  usStatesFC?: AirshedAggregateResult["featureCollection"];
}

export const DEFAULT_SECTIONS: Record<SectionKey, boolean> = {
  cover: true,
  executive_summary: true,
  fuel_displacement: true,
  emissions_air: true,
  emissions_water: true,
  emissions_soil: true,
  emissions_resources: true,
  public_health: true,
  biodiversity: true,
  economic: true,
  spatial_map: false,
  ranking_absolute: true,
  ranking_per_mw: false,
  data_quality: false,
  methodology: true,
  glossary: true,
  closing: true,
};
